import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  Input,
} from "@heroui/react";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/api";
import useErrorHandling from "@/hooks/useErrorHandling";
import { toast } from "react-toastify";
import rootStore from "@/app/store";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { S3_URL } from "@/api/s3";
import { useVirtualizer } from "@tanstack/react-virtual";
import { NewLocationHandbook } from "../handbooks/locationHandbook";

interface SelectUploadedFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (urls: string[]) => void;
  title?: string;
  multiple?: boolean;
  searchParams?: {
    [key: string]: string | number | boolean;
  };
}

export function SelectUploadedFilesModal({
  isOpen,
  onClose,
  onFilesSelected,
  title = "Выбрать из загруженных файлов",
  multiple = true,
  searchParams = {},
}: SelectUploadedFilesModalProps) {
  const [files, setFiles] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState<
    string | undefined
  >(undefined);

  const handleError = useErrorHandling();
  const parentRef = useRef<HTMLDivElement>(null);

  const businessProfileId =
    rootStore.getState().businessProfile.businessProfile.id;

  const getFileName = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.split("/").pop() || "Файл";
    } catch {
      return url;
    }
  };

  const getFileExtension = useCallback((url: string) => {
    const fileName = getFileName(url);
    return fileName.includes(".")
      ? fileName.split(".").pop()?.toLowerCase() || ""
      : "";
  }, []);

  const getFileType = (extension: string) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const documentExtensions = ["pdf", "doc", "docx", "txt", "rtf"];
    const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv"];

    if (imageExtensions.includes(extension)) return "image";
    if (documentExtensions.includes(extension)) return "document";
    if (videoExtensions.includes(extension)) return "video";
    return "other";
  };

  const isImageFile = (url: string) => {
    const extension = getFileExtension(url);
    return ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension);
  };

  const filteredFiles = useMemo(() => {
    return files.filter((fileUrl) => {
      const fileName = getFileName(fileUrl);
      const fileExtension = getFileExtension(fileUrl);

      const matchesSearch =
        searchQuery === "" ||
        fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        fileUrl.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesParams = Object.entries(searchParams).every(
        ([key, value]) => {
          switch (key) {
            case "extension":
              return (
                fileExtension.toLowerCase() === String(value).toLowerCase()
              );
            case "type":
              return getFileType(fileExtension) === String(value);
            default:
              return true;
          }
        },
      );

      return matchesSearch && matchesParams;
    });
  }, [files, getFileExtension, searchQuery, searchParams]);

  const rowVirtualizer = useVirtualizer({
    count: filteredFiles.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 96,
    overscan: 5,
  });

  const loadFiles = useCallback(async () => {
    if (!businessProfileId) return;

    setIsLoading(true);
    try {
      const response = await api.s3.listFiles(
        businessProfileId.toString(),
        selectedLocationId,
      );
      if (response.data) {
        setFiles(response.data);
      }
    } catch (error) {
      const parsedError = handleError(error as unknown as Error);
      toast.error(parsedError.errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [businessProfileId, handleError, selectedLocationId]);

  useEffect(() => {
    if (isOpen) {
      loadFiles();
      setSelectedFiles([]);
    }
  }, [isOpen, loadFiles]);

  const handleFileToggle = (fileUrl: string) => {
    if (multiple) {
      setSelectedFiles((prev) =>
        prev.includes(fileUrl)
          ? prev.filter((url) => url !== fileUrl)
          : [...prev, fileUrl],
      );
    } else {
      setSelectedFiles([fileUrl]);
    }
  };

  const handleConfirm = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      classNames={{
        backdrop: "z-[600]",
        wrapper: "z-[600]",
      }}
      backdrop="blur"
      scrollBehavior="outside"
      isDismissable={false}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">{title}</ModalHeader>
        <ModalBody>
          <div className="mb-4 flex flex-col gap-2">
            <Input
              placeholder="Поиск файлов..."
              value={searchQuery}
              variant="bordered"
              onChange={(e) => setSearchQuery(e.target.value)}
              startContent={<FaMagnifyingGlass className="text-gray-400" />}
            />
            <NewLocationHandbook
              children={null}
              isFinancial={false}
              onSelectionChange={(e) =>
                setSelectedLocationId(e?.toString() || undefined)
              }
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {files.length === 0
                ? "Нет загруженных файлов"
                : "Файлы не найдены"}
            </div>
          ) : (
            <div
              ref={parentRef}
              className="max-h-96 overflow-auto"
              style={{
                height: `${Math.min(filteredFiles.length * 80, 384)}px`,
              }}
            >
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const fileUrl = filteredFiles[virtualRow.index];
                  return (
                    <div
                      key={virtualRow.index}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${virtualRow.size - 8}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                      className="mb-2"
                    >
                      <div
                        className="flex items-center space-x-3 p-3 border-2 rounded-lg hover:bg-gray-50 cursor-pointer h-full"
                        onClick={() => handleFileToggle(fileUrl)}
                      >
                        <Checkbox
                          isSelected={selectedFiles.includes(fileUrl)}
                          onChange={() => handleFileToggle(fileUrl)}
                        />

                        {isImageFile(fileUrl) && (
                          <div className="w-12 h-12 flex-shrink-0">
                            <img
                              src={`${S3_URL}/${fileUrl}`}
                              alt={getFileName(fileUrl)}
                              className="w-full h-full object-cover rounded border"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                              }}
                            />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {getFileName(fileUrl)}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {fileUrl}
                          </p>
                          <p className="text-xs text-gray-400">
                            {getFileExtension(fileUrl).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            Отмена
          </Button>
          <Button
            color="primary"
            onPress={handleConfirm}
            isDisabled={selectedFiles.length === 0}
          >
            {multiple ? `Выбрать (${selectedFiles.length})` : "Выбрать"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
